import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import Map from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import * as olProj from 'ol/proj';
import Overlay from 'ol/Overlay';
import Point from 'ol/geom/Point';
import { Feature } from 'ol';
import { Circle, Fill, Style } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { fromLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control.js';
import { DataRepositoryService } from '../../services/data-repository/data-repository.service';
import { FormControl } from '@angular/forms';
import { LookupService } from 'src/app/modules/lookup/components/lookup/lookup.service';
import { debounceTime, distinctUntilChanged, startWith, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';



@Component({
    selector: "app-address-details",
    templateUrl: "./address-details.component.html",
    styleUrls: [
        "./address-details.component.scss",
        "../../../shared/css/primeng-table.component.scss",
        "../../../shared/css/primeng-autocomplete.component.scss",
    ],
    standalone: false
})
export class AddressDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() addressData: any;
  @Input() isLookup: boolean;
  @Output() searchAddressEvent = new EventEmitter();
  errorMessage: "";
  addressSearch: any = new FormControl(null);
  suggestedSelectedAddress: any;

  tableData: { label: string; value: string }[] = [];

  constructor(
    private readonly dataRepositoryService: DataRepositoryService,
    public lookupService: LookupService,
    private activateRoute: ActivatedRoute,
    private router: Router
  ) {
    this.addressSearch.valueChanges
      .pipe(startWith(""), debounceTime(300), distinctUntilChanged())
      .subscribe((val: any) => {
        if (val) {
          this.lookupService.getSearchResults(val);
        } else {
          this.lookupService.filteredOptions = [];
        }
      });
  }

  map: Map;

  ngOnInit(): void {
    setTimeout(() => {
      this.initMap();
    }, 100);
    this.initializeTable();
  }

  ngAfterViewInit(): void {
    this.activateRoute.queryParams.pipe(take(1)).subscribe((params: any) => {
      if (params?.pam_id) {
        this.addressSearch.setValue(params?.pam_id)
        this.searchAddressEvent.emit(this.addressSearch.value);
        // Clear the one-shot query param to avoid re-triggering when the component is recreated (e.g. switching tabs)
        this.router.navigate([], {
          relativeTo: this.activateRoute,
          queryParams: { pam_id: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    })
  }


  initializeTable(): void {
    const data = this.addressData ?? {};
    this.tableData = [
      { label: "Source", value: data.source ?? "" },
      { label: "Address ID", value: data.address_id ?? "" },
      { label: "Street", value: data.street_name ?? "" },
      { label: "Address Number", value: data.address_number ?? "" },
      ...(data.address_number_suffix
        ? [
            {
              label: "Address Number Suffix",
              value: data.address_number_suffix,
            },
          ]
        : []),
      ...(data.floor_identifier
        ? [{ label: "Floor Identifier", value: data.floor_identifier }]
        : []),
      ...(data.door_identifier
        ? [{ label: "Door Identifier", value: data.door_identifier }]
        : []),
      { label: "City", value: data.city ?? "" },
      { label: "Postal Code", value: data.postal_code ?? "" },
      { label: "Country", value: data.country ?? "" },
      {
        label: "Door Delivery Required (P9)",
        value: data.door_delivery_required ?? "",
      },
      {
        label: "Receiver Flex Location",
        value: data.receiver_flex_location ?? "",
      },
      {
        label: "Default Service Point Name",
        value: data.default_service_point_name ?? "",
      },
      {
        label: "Address Coordinate",
        value: this.formatAddressCoords(data.coordinate),
      },
      {
        label: "Route Point Coordinate",
        value: this.formatAddressCoords(data.route_point_coordinate),
      },
      ...(this.isLookup
        ? [
            {
              label: "Route",
              value: data.routes?.route_label || "",
            },
            {
              label: "Username",
              value: data.routes?.worker_sessions?.[0]?.user_name ?? "",
            },
            {
              label: "Device",
              value: data.routes?.worker_sessions?.[0]?.meta?.device_id ?? "",
            },
          ]
        : []),
    ];
  }

  /**
   * Method sets the event object to a class variable
   * @param $event object of autocomplete control contains the selected option 
   */
  onAddressSelect($event: any) {
    this.suggestedSelectedAddress = $event;
    // p-autoComplete writes the whole suggestion object into the form control by default
    // (`field` only controls what is displayed). Keep only the readable label as the control
    // value so `addressSearch.value` stays a string; the object is retained in suggestedSelectedAddress.
    this.addressSearch.setValue($event?.properties?.label ?? "", { emitEvent: false });
  }

  /**
   * search based on lookup type or the addresses
   */
  searchResult($event?:Event):void {
    if (this.addressSearch.value) {
      if (this.suggestedSelectedAddress) {
        this.formatAddress(this.suggestedSelectedAddress);
      } else {
        this.searchAddressEvent.emit(this.addressSearch.value);
      }
    }

    //remove the focus from the icon using $event
    const searchIconElement = $event?.target as HTMLElement | undefined;
    searchIconElement?.blur();
  }

  /**
   * Method formats the address object and emits to parent component
   * @param data object of the selected address option from the suggestion
   */
  formatAddress(data: any) {
    // Emit the address id only if the current input still matches the selected suggestion's label;
    // otherwise treat the input as free-text search.
    if (this.addressSearch.value === data?.properties?.label) {
      if (data.properties.id) {
        this.searchAddressEvent.emit(data.properties.id);
      } else {
        this.searchAddressEvent.emit(
          data.properties.name ? data.properties.name : ""
        );
      }
    } else {
      this.searchAddressEvent.emit(this.addressSearch.value ?? "");
    }
  }

  formatAddressCoords(coordinates: any) {
    return coordinates?.latitude && coordinates?.longitude
        ? `Lat: ${coordinates.latitude}, Lng: ${coordinates.longitude}`
        : ""
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * Verify the addressData input prop and then set the location Pointer on OSM Map
     */
    if (this.addressData && Object.entries(this.addressData).length) {
      /**
       * Clear the map other layer before plotting new coordinates
       */
      this.initializeTable();
      this.removeAllLayers();
      this.addLocationPointer();
    } else {
      this.removeAllLayers();
      this.initializeTable();
    }
  }

  /**
   * Method initializes the OSM Map view inside DOM
   */
  initMap(): void {
    this.map = new Map({
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      controls: defaultControls({ attribution: false }),
      target: "address-map",
      view: new View({
        center: olProj.fromLonLat([
          Number(this.dataRepositoryService.blCoordinates?.longitude),
          Number(this.dataRepositoryService.blCoordinates?.latitude),
        ]),
        zoom: 17,
      }),
    });
  }

  /**
   * Method sets the location pointer based on the location coordinate.
   * This method will locate the pointer in the specific coordinate which is
   * received from the address details object
   * address details object information received by calling searchAddressByAddressId method
   */
  addLocationPointer() {
    let routecoordinate = [
      this.addressData?.route_point_coordinate?.longitude,
      this.addressData?.route_point_coordinate?.latitude,
    ];
    let centerRoute = olProj.fromLonLat(routecoordinate);
    let routeMarker: any = new Overlay({
      id: 123,
      position: centerRoute,
      element: document.getElementById(`routeLocation`) ?? undefined,
      stopEvent: false,
    });

    if (this.addressData?.route_point_coordinate) {
      this.map.addOverlay(routeMarker);
    }

    let addrcoordinate = [
      this.addressData?.coordinate?.longitude,
      this.addressData?.coordinate?.latitude,
    ];
    let addrMarker = new Overlay({
      id: 234,
      position: olProj.fromLonLat(addrcoordinate),
      element: document.getElementById(`AddrLocation`) ?? undefined,
      stopEvent: false
    });

    if (this.addressData?.coordinate) {
      this.map.addOverlay(addrMarker);
    }

    let center;
    if (addrcoordinate[0] !== undefined) {
      center = addrcoordinate;
    } else if (routecoordinate[0] !== undefined) {
      center = routecoordinate;
    } else {
      center = [10.01, 56.981];
    }

    this.map.getView().setZoom(14);
    center = olProj.fromLonLat(center);
    this.map.getView().setCenter(center);

    const coordinates = [
      { coord: routecoordinate, color: '#2f6db4', radius: 7 },
      { coord: addrcoordinate, color: '#ffca2c', radius: 7 },
    ];

    coordinates.forEach(({ coord, color, radius }) => {
      const marker = new Feature({
        geometry: new Point(fromLonLat(coord)),
      });

      const markerStyle = new Style({
        image: new Circle({
          radius: radius,
          fill: new Fill({ color: color }),
        }),
      });

      marker.setStyle(markerStyle);

      const vectorSource = new VectorSource({
        features: [marker],
      });

      const markerLayer = new VectorLayer({
        source: vectorSource,
      });

      this.map.addLayer(markerLayer);
    });
  }

  /**
   * Function to remove all the plotted layers (vector + tile layers) on map,
   * and adds tile layer, sets the zoom and center as default view of map
   */
  removeAllLayers(){
    let emptyLayer:any = [];
    this.map?.getLayers().extend(emptyLayer);
    let baseLayer = new TileLayer({
      source: new OSM()
    })

    this.map?.addLayer(baseLayer);
  }

  handleClearSearchedText(){
    this.addressSearch.reset();
    this.addressData = {};
    this.initializeTable()
  }

  ngOnDestroy(): void {
    this.removeAllLayers();
  }

}